import { useEffect, useRef, useState, useCallback } from 'react';
import logger from '../utils/logger';

export const useMultiplayerSocket = (gameId, userId, onMoveReceived, onThemeChanged, onGameOver, onPlayerDisconnected, onRematchUpdate, isSpectator = false, onSpectatorCountChange = null, onSpectatorRequest = null, onRejoinRequest = null, onGameStateReceived = null) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const pingIntervalRef = useRef(null);
  const isUnmountedRef = useRef(false);

  // Store callbacks in refs to avoid dependency issues
  const onMoveReceivedRef = useRef(onMoveReceived);
  const onThemeChangedRef = useRef(onThemeChanged);
  const onGameOverRef = useRef(onGameOver);
  const onPlayerDisconnectedRef = useRef(onPlayerDisconnected);
  const onRematchUpdateRef = useRef(onRematchUpdate);
  const isSpectatorRef = useRef(isSpectator);
  const onSpectatorCountChangeRef = useRef(onSpectatorCountChange);
  const onSpectatorRequestRef = useRef(onSpectatorRequest);
  const onRejoinRequestRef = useRef(onRejoinRequest);
  const onGameStateReceivedRef = useRef(onGameStateReceived);

  useEffect(() => {
    onMoveReceivedRef.current = onMoveReceived;
    onThemeChangedRef.current = onThemeChanged;
    onGameOverRef.current = onGameOver;
    onPlayerDisconnectedRef.current = onPlayerDisconnected;
    onRematchUpdateRef.current = onRematchUpdate;
    isSpectatorRef.current = isSpectator;
    onSpectatorCountChangeRef.current = onSpectatorCountChange;
    onSpectatorRequestRef.current = onSpectatorRequest;
    onRejoinRequestRef.current = onRejoinRequest;
    onGameStateReceivedRef.current = onGameStateReceived;
  }, [onMoveReceived, onThemeChanged, onGameOver, onPlayerDisconnected, onRematchUpdate, isSpectator, onSpectatorCountChange, onSpectatorRequest, onRejoinRequest, onGameStateReceived]);

  useEffect(() => {
    if (!gameId || !userId) return;
    
    isUnmountedRef.current = false;

    const connectWebSocket = () => {
      if (isUnmountedRef.current) return;
      
      // Construct WebSocket URL from backend URL
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
      const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
      const WS_URL = `${wsProtocol}://${wsHost}/api/ws/game/${gameId}`;
      // Use native WebSocket (FastAPI uses native WebSockets, not Socket.IO)
      const ws = new WebSocket(WS_URL);
      socketRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectAttempts.current = 0;

        // Authenticate user immediately after connection
        ws.send(JSON.stringify({
          type: 'auth',
          user_id: userId,
          is_spectator: isSpectatorRef.current
        }));
        // Setup ping interval to keep connection alive
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000); // Ping every 25 seconds
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'player_joined':
              break;

            case 'move_made':
              // Spectators should see ALL moves, players should skip their own
              if (isSpectatorRef.current || data.user_id !== userId) {
                if (onMoveReceivedRef.current) {
                  onMoveReceivedRef.current(data.move, data.board_state);
                }
              }
              break;

            case 'board_update':
              if (onMoveReceivedRef.current) {
                onMoveReceivedRef.current(data.move, data.board_state);
              }
              break;

            case 'theme_changed':
              if (onThemeChangedRef.current) {
                onThemeChangedRef.current(data.theme);
              }
              break;

            case 'game_over':
              if (onGameOverRef.current) {
                onGameOverRef.current(data.winner, data.reason || 'checkmate');
              }
              break;

            case 'player_forfeited':
              // Treat forfeit as game over - notify the callback with winner and actual reason
              if (onGameOverRef.current) {
                // Use the reason from data (can be 'resignation' or 'forfeit'), default to 'forfeit'
                const gameOverReason = data.reason || 'forfeit';
                onGameOverRef.current(data.winner, gameOverReason, data.message);
              } else {
                logger.warn('⚠️ onGameOverRef.current is null/undefined - game over callback not triggered');
              }
              break;

            case 'player_disconnected':
              if (onPlayerDisconnectedRef.current) {
                onPlayerDisconnectedRef.current(data.user_id);
              }
              break;

            case 'spectator_joined':
              setSpectatorCount(prev => {
                const newCount = prev + 1;
                if (onSpectatorCountChangeRef.current) {
                  onSpectatorCountChangeRef.current(newCount);
                }
                return newCount;
              });
              break;

            case 'spectator_disconnected':
              setSpectatorCount(prev => {
                const newCount = Math.max(0, prev - 1);
                if (onSpectatorCountChangeRef.current) {
                  onSpectatorCountChangeRef.current(newCount);
                }
                return newCount;
              });
              break;

            case 'spectator_count':
              setSpectatorCount(data.count);
              if (onSpectatorCountChangeRef.current) {
                onSpectatorCountChangeRef.current(data.count);
              }
              break;
            
            case 'pong':
              // Server responded to ping, connection is alive
              break;

            // Rematch handling
            case 'rematch_requested':
              if (onRematchUpdateRef.current) {
                // If the request is from someone else, show opponent_requested
                if (data.user_id !== userId) {
                  onRematchUpdateRef.current('opponent_requested');
                }
              }
              break;

            case 'rematch_accepted':
              if (onRematchUpdateRef.current) {
                onRematchUpdateRef.current('accepted', data.board_state);
              }
              break;

            case 'rematch_declined':
              if (onRematchUpdateRef.current) {
                onRematchUpdateRef.current('declined');
              }
              break;

            case 'spectator_request':
              if (onSpectatorRequestRef.current) {
                onSpectatorRequestRef.current(data);
              }
              break;

            case 'rejoin_request':
              if (onRejoinRequestRef.current) {
                onRejoinRequestRef.current(data);
              }
              break;

            case 'game_state':
              // Full game state received (sent to spectators on connect)
              if (onGameStateReceivedRef.current) {
                onGameStateReceivedRef.current(data);
              }
              break;

            case 'turn_skipped':
              // A player ran out of time and their turn was skipped
              // Show notification
              if (data.message) {
              }
              // Update board state immediately to show whose turn it is
              if (data.board_state && onMoveReceivedRef.current) {
                // Pass the board_state - handleMoveReceived will update gameState
                // Then we need to refetch to get legal moves
                onMoveReceivedRef.current(null, data.board_state);
              }
              // Signal that we need to refetch game state to get fresh legal_moves
              // This is done via onGameStateReceived with refetch_needed flag
              if (onGameStateReceivedRef.current) {
                onGameStateReceivedRef.current({ refetch_needed: true });
              }
              break;

            default:
          }
        } catch (error) {
          logger.error('❌ Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        logger.error('❌ WebSocket error:', error);
        setConnected(false);
      };

      ws.onclose = (event) => {
        setConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Attempt reconnection only if not unmounted
        if (!isUnmountedRef.current && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
          setTimeout(connectWebSocket, delay);
        } else if (!isUnmountedRef.current) {
          logger.error('❌ Max reconnection attempts reached');
        }
      };
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      isUnmountedRef.current = true;
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [gameId, userId]);

  const sendMove = useCallback((move, boardState) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'move',
        move: move,
        board_state: boardState
      }));
    } else {
      logger.warn('⚠️ WebSocket not connected, cannot send move');
    }
  }, []);

  const sendThemeChange = useCallback((theme) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'theme_change',
        theme: theme
      }));
    }
  }, []);

  const sendGameOver = useCallback((winner) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'game_over',
        winner: winner
      }));
    }
  }, []);

  // Rematch functions
  const sendRematchRequest = useCallback(() => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'rematch_request'
      }));
    }
  }, []);

  const sendRematchDecline = useCallback(() => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'rematch_decline'
      }));
    }
  }, []);

  return {
    connected,
    socketRef,
    sendMove,
    sendThemeChange,
    sendGameOver,
    sendRematchRequest,
    sendRematchDecline,
    spectatorCount
  };
};
